import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { ProductDetailDto } from "@hisense/shared";
import { useProductLots, useProductStockHistory } from "@/api/productStock";
import { LotLabelsButton } from "@/components/warehouse/LotLabelsButton";
import { formatDateTime } from "@/domain/datetime";
import { formatQty } from "@/domain/money";
import { Button, buttonStyles } from "@/components/ui/Button";
import { TableShell, Td, Th, Tr } from "@/components/ui/Table";

/**
 * Tồn kho của một sản phẩm theo từng lô (18/09/2026: Kho & LOT là nguồn duy nhất).
 *
 * Không còn ô gõ tay số tồn: tồn chỉ đổi qua chứng từ kho, nên trang này chỉ đọc và chỉ đường sang đúng
 * chỗ làm chứng từ (kiểm kê / tồn đầu kỳ, số dư theo lô). Điều chỉnh tay có từ trước ngày đó vẫn hiện để tra.
 */
export function ProductStockPanel({ product }: { product: ProductDetailDto }) {
  const { t, i18n } = useTranslation();
  const lots = useProductLots(product.id);
  const history = useProductStockHistory(product.id);
  const unit = product.unit ?? "";
  const b = product.stockBreakdown;
  const figures: [string, number, string][] = [
    [t("productStock.available"), b.available, "text-ink"],
    [t("productStock.pending"), b.pending, "text-warn"],
    [t("productStock.rejected"), b.rejected, "text-danger"],
    [t("productStock.onHand"), b.onHand, "text-ink-soft"],
  ];
  const adjustments = history.data?.adjustments ?? [];
  const showSupplier = lots.data?.some((l) => l.supplierName) ?? false;

  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="eyebrow text-ink-faint">{t("productStock.title")}</h2>
            <p className="mt-1 max-w-2xl text-xs text-ink-soft">{t("productStock.hint")}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/stock-moves/counts" className={buttonStyles({ variant: "secondary", size: "sm" })}>{t("productStock.goCounts")}</Link>
            <Link to="/warehouse?tab=balances" className={buttonStyles({ variant: "ghost", size: "sm" })}>{t("productStock.goBalances")}</Link>
          </div>
        </div>
        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {figures.map(([label, value, tone]) => (
            <div key={label} className="rounded-md border border-line bg-surface px-3 py-2">
              <dt className="text-2xs uppercase tracking-wide text-ink-faint">{label}</dt>
              <dd className={`font-mono tabular text-xl ${tone}`}>
                {formatQty(value)} <span className="text-xs text-ink-faint">{unit}</span>
              </dd>
            </div>
          ))}
        </dl>
        {b.otherUnits.map((o) => (
          <p key={o.unit} className="text-xs text-warn">
            {t("productStock.otherUnits", { unit: o.unit, onHand: formatQty(o.onHand), available: formatQty(o.available) })}
          </p>
        ))}
        {product.legacyStockQuantity !== 0 && (
          <p className="text-xs text-ink-soft">{t("productStock.legacy", { quantity: formatQty(product.legacyStockQuantity), unit })}</p>
        )}
      </section>

      <section className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-ink">{t("productStock.lots")}</h3>
          {!!lots.data?.length && (
            <LotLabelsButton lots={lots.data.map((l) => ({ productCode: product.productCode, subRef: product.subRef, description: product.description,
              descriptionZh: product.descriptionZh, color: product.color, colorZh: product.colorZh, size: product.size, lotNumber: l.lotNumber }))} />
          )}
        </div>
        {lots.isLoading ? (
          <p className="text-sm text-ink-soft">{t("productStock.loading")}</p>
        ) : lots.error ? (
          <div className="flex items-center gap-3 text-sm text-danger">
            <span role="alert">{t("productStock.error")}</span>
            <Button size="sm" variant="secondary" onClick={() => void lots.refetch()}>{t("productStock.retry")}</Button>
          </div>
        ) : !lots.data?.length ? (
          <p className="rounded-md bg-sunken px-3 py-3 text-sm text-ink-soft">{t("productStock.empty")}</p>
        ) : (
          <TableShell>
            <thead>
              <tr>
                <Th>{t("productStock.warehouse")}</Th>
                <Th>{t("productStock.location")}</Th>
                <Th>{t("productStock.lot")}</Th>
                <Th>{t("productStock.source")}</Th>
                {showSupplier && <Th>{t("productStock.supplier")}</Th>}
                <Th align="right">{t("productStock.available")}</Th>
                <Th align="right" title={t("productStock.pending")}>{t("productStock.pendingShort")}</Th>
                <Th align="right">{t("productStock.rejected")}</Th>
                <Th align="right">{t("productStock.onHand")}</Th>
              </tr>
            </thead>
            <tbody>
              {lots.data.map((l) => (
                <Tr key={`${l.warehouseCode}|${l.location}|${l.lotNumber}|${l.unit}|${l.kind}`}>
                  <Td>{l.warehouseName}{l.warehouseCode && <span className="ml-1 font-mono text-2xs text-ink-faint">{l.warehouseCode}</span>}</Td>
                  <Td className="font-mono tabular text-xs">{l.location}</Td>
                  <Td className="font-mono tabular text-xs">{l.lotNumber}</Td>
                  <Td className="text-xs text-ink-soft">{t(`productStock.kinds.${l.kind}`, { defaultValue: l.kind })}</Td>
                  {showSupplier && <Td className="text-xs text-ink-soft">{l.supplierName ?? "—"}</Td>}
                  <Td align="right" className="font-mono tabular">{formatQty(l.available)} <span className="text-2xs text-ink-faint">{l.unit}</span></Td>
                  <Td align="right" className="font-mono tabular text-warn">{l.pending ? formatQty(l.pending) : "—"}</Td>
                  <Td align="right" className="font-mono tabular text-danger">{l.rejected ? formatQty(l.rejected) : "—"}</Td>
                  <Td align="right" className="font-mono tabular text-ink-soft">{formatQty(l.onHand)}</Td>
                </Tr>
              ))}
            </tbody>
          </TableShell>
        )}
      </section>

      {history.error ? (
        <p role="alert" className="text-sm text-danger">{t("productStock.historyError")}</p>
      ) : adjustments.length > 0 ? (
        <details className="rounded-md border border-line">
          <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-ink-soft">{t("productStock.history")} ({adjustments.length})</summary>
          <TableShell className="rounded-none border-0 border-t">
            <thead>
              <tr>
                <Th>{t("productStock.number")}</Th>
                <Th align="right">{t("productStock.before")}</Th>
                <Th align="right">{t("productStock.after")}</Th>
                <Th>{t("productStock.reasonColumn")}</Th>
                <Th>{t("productStock.operator")}</Th>
                <Th>{t("productStock.time")}</Th>
              </tr>
            </thead>
            <tbody>
              {adjustments.map((a) => (
                <Tr key={a.id}>
                  <Td className="font-mono tabular text-xs">{a.moveNo}</Td>
                  <Td align="right" className="font-mono tabular">{formatQty(a.beforeQuantity)}</Td>
                  <Td align="right" className="font-mono tabular">{formatQty(a.afterQuantity)}</Td>
                  <Td className="text-xs">{a.reason}</Td>
                  <Td className="text-xs">{a.actorName}</Td>
                  <Td className="font-mono tabular text-xs">{formatDateTime(a.movedAt, i18n.language)}</Td>
                </Tr>
              ))}
            </tbody>
          </TableShell>
        </details>
      ) : null}
    </div>
  );
}
